import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Upload, Link2, FileText, RefreshCw, Trash2, Eye, Search, Plus, Database } from "lucide-react";
import { api, errMsg, fmtDate } from "@/lib/api";
import { PageHead, Stat, Panel } from "./AdminLayout";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

export const KB_CATS = { regulasi: "Regulasi Konstruksi", persyaratan: "Persyaratan Berkas", matrix_estimasi: "Matrix Estimasi & Alur", profil: "Profil Perusahaan", lainnya: "Lainnya" };
const SRC_ICON = { pdf: FileText, url: Link2, text: FileText, file: FileText };
const inputCls = "mt-1 w-full border border-slate-300 px-3 py-2 text-sm outline-none focus:border-[#D97706]";

const CatSelect = ({ value, onChange, testid }) => (
  <select value={value} onChange={(e) => onChange(e.target.value)} className={inputCls} data-testid={testid}>
    {Object.entries(KB_CATS).map(([k, l]) => <option key={k} value={k}>{l}</option>)}
  </select>
);

function AddDialog({ open, onClose, onAdded }) {
  const [cat, setCat] = useState("regulasi");
  const [title, setTitle] = useState("");
  const [url, setUrl] = useState("");
  const [content, setContent] = useState("");
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const run = async (fn) => {
    setBusy(true);
    try {
      const { data } = await fn();
      toast.success(`Terindeks: ${data.chunk_count} chunk`);
      onAdded(data);
      setTitle(""); setUrl(""); setContent(""); setFile(null);
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  const upload = () => {
    if (!file) return toast.error("Pilih file terlebih dahulu");
    const fd = new FormData();
    fd.append("file", file); fd.append("category", cat);
    if (title) fd.append("title", title);
    run(() => api.post("/admin/kb/upload", fd));
  };
  const btn = "mt-4 w-full bg-[#0F2B48] py-3 text-sm font-bold text-white hover:bg-[#D97706] disabled:opacity-50";
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-xl rounded-none" data-testid="kb-add-dialog">
        <DialogHeader><DialogTitle>Tambah Dokumen Knowledge Base</DialogTitle></DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          <label className="text-xs font-semibold">Kategori<CatSelect value={cat} onChange={setCat} testid="kb-add-category" /></label>
          <label className="text-xs font-semibold">Judul (opsional untuk PDF/URL)<input value={title} onChange={(e) => setTitle(e.target.value)} className={inputCls} data-testid="kb-add-title" /></label>
        </div>
        <Tabs defaultValue="pdf">
          <TabsList className="w-full rounded-none">
            <TabsTrigger value="pdf" className="flex-1 rounded-none" data-testid="kb-tab-pdf">PDF / TXT</TabsTrigger>
            <TabsTrigger value="url" className="flex-1 rounded-none" data-testid="kb-tab-url">URL</TabsTrigger>
            <TabsTrigger value="text" className="flex-1 rounded-none" data-testid="kb-tab-text">Teks Manual</TabsTrigger>
          </TabsList>
          <TabsContent value="pdf">
            <label className="mt-2 flex cursor-pointer flex-col items-center gap-2 border-2 border-dashed border-slate-300 p-8 text-center text-sm text-slate-500 hover:border-[#D97706]">
              <Upload size={24} /> {file ? <b className="text-[#0F2B48]">{file.name}</b> : "Klik untuk memilih PDF, TXT, atau MD (maks 15MB)"}
              <input type="file" accept=".pdf,.txt,.md" className="hidden" onChange={(e) => setFile(e.target.files[0])} data-testid="kb-upload-file-input" />
            </label>
            <button disabled={busy} onClick={upload} className={btn} data-testid="kb-upload-pdf-button">{busy ? "Mengindeks..." : "Unggah & Indeks"}</button>
          </TabsContent>
          <TabsContent value="url">
            <input value={url} onChange={(e) => setUrl(e.target.value)} placeholder="https://jdih.pu.go.id/..." className={inputCls} data-testid="kb-url-input" />
            <button disabled={busy || !url} onClick={() => run(() => api.post("/admin/kb/url", { url, category: cat, title: title || undefined }))} className={btn} data-testid="kb-url-submit">{busy ? "Mengambil & mengindeks..." : "Ambil Konten & Indeks"}</button>
          </TabsContent>
          <TabsContent value="text">
            <textarea rows={8} value={content} onChange={(e) => setContent(e.target.value)} placeholder="Tempel isi regulasi, SOP, atau persyaratan..." className={inputCls} data-testid="kb-text-input" />
            <button disabled={busy || !title || content.length < 10} onClick={() => run(() => api.post("/admin/kb/text", { title, category: cat, content }))} className={btn} data-testid="kb-text-submit">{busy ? "Mengindeks..." : "Simpan & Indeks"}</button>
          </TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
}

function DocDialog({ docId, onClose, onUpdated }) {
  const [doc, setDoc] = useState(null);
  const [edit, setEdit] = useState({});
  useEffect(() => {
    if (!docId) return setDoc(null);
    api.get(`/admin/kb/${docId}`).then((r) => { setDoc(r.data); setEdit({ title: r.data.title, category: r.data.category, content: r.data.content }); });
  }, [docId]);
  const save = async () => {
    try {
      const { data } = await api.put(`/admin/kb/${docId}`, edit);
      toast.success(`Disimpan & re-index: ${data.chunk_count} chunk`);
      onUpdated(data);
      const r = await api.get(`/admin/kb/${docId}`);
      setDoc(r.data);
    } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <Dialog open={!!docId} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[92vh] max-w-4xl overflow-y-auto rounded-none" data-testid="kb-doc-dialog">
        <DialogHeader><DialogTitle>Editor Dokumen</DialogTitle></DialogHeader>
        {doc && (
          <div className="grid gap-6 lg:grid-cols-5">
            <div className="space-y-3 lg:col-span-3">
              <input value={edit.title} onChange={(e) => setEdit({ ...edit, title: e.target.value })} className={inputCls} data-testid="kb-edit-title" />
              <CatSelect value={edit.category} onChange={(v) => setEdit({ ...edit, category: v })} testid="kb-edit-category" />
              <textarea rows={16} value={edit.content} onChange={(e) => setEdit({ ...edit, content: e.target.value })} className={`${inputCls} font-mono text-xs`} data-testid="kb-edit-content" />
              <button onClick={save} className="w-full bg-[#0F2B48] py-3 text-sm font-bold text-white hover:bg-[#D97706]" data-testid="kb-edit-save">Simpan & Re-index</button>
            </div>
            <div className="lg:col-span-2">
              <p className="eyebrow mb-2">Chunk Terindeks ({doc.chunks.length})</p>
              <div className="max-h-[520px] space-y-2 overflow-y-auto">
                {doc.chunks.map((c) => (
                  <div key={c.id} className="border border-slate-200 bg-slate-50 p-3" data-testid={`kb-chunk-${c.chunk_index}`}>
                    <p className="mb-1 font-mono text-[10px] text-slate-500">#{c.chunk_index} · {c.char_count} karakter</p>
                    <p className="line-clamp-5 text-xs text-slate-700">{c.content}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function RetrievalTester() {
  const [q, setQ] = useState("");
  const [res, setRes] = useState(null);
  const run = async (e) => {
    e.preventDefault();
    const { data } = await api.post("/admin/kb-search", { query: q });
    setRes(data);
  };
  return (
    <Panel title="Uji Retrieval (RAG Tester)">
      <form onSubmit={run} className="flex">
        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="mis. syarat SBU menengah" className="flex-1 border border-slate-300 px-3 py-2 text-sm outline-none" data-testid="kb-search-input" />
        <button disabled={q.length < 2} className="bg-[#D97706] px-3 text-white disabled:opacity-50" data-testid="kb-search-button"><Search size={14} /></button>
      </form>
      <div className="mt-3 max-h-96 space-y-2 overflow-y-auto" data-testid="kb-search-results">
        {res?.map((c, i) => (
          <div key={c.id} className="border-l-2 border-[#0284C7] bg-slate-50 p-2.5">
            <p className="font-mono text-[10px] text-slate-500">#{i + 1} · skor {c.score.toFixed(2)} · {KB_CATS[c.category]}</p>
            <p className="text-xs font-semibold text-[#0F2B48]">{c.title}</p>
            <p className="line-clamp-3 text-xs text-slate-600">{c.content}</p>
          </div>
        ))}
        {res && !res.length && <p className="text-xs text-slate-500">Tidak ada chunk relevan.</p>}
      </div>
    </Panel>
  );
}

export default function KnowledgeBase() {
  const [docs, setDocs] = useState([]);
  const [cat, setCat] = useState("all");
  const [adding, setAdding] = useState(false);
  const [viewId, setViewId] = useState(null);
  useEffect(() => { api.get("/admin/kb").then((r) => setDocs(r.data)); }, []);
  const upsert = (d) => setDocs((x) => (x.some((i) => i.id === d.id) ? x.map((i) => (i.id === d.id ? d : i)) : [d, ...x]));
  const shown = cat === "all" ? docs : docs.filter((d) => d.category === cat);
  const totalChunks = useMemo(() => docs.reduce((s, d) => s + (d.chunk_count || 0), 0), [docs]);

  const toggle = async (d) => upsert((await api.put(`/admin/kb/${d.id}`, { active: !d.active })).data);
  const reindex = async (d) => { const { data } = await api.post(`/admin/kb/${d.id}/reindex`); upsert({ ...d, chunk_count: data.chunk_count, status: "indexed" }); toast.success("Re-index selesai"); };
  const del = async (d) => { if (!window.confirm(`Hapus "${d.title}" beserta seluruh chunk?`)) return; await api.delete(`/admin/kb/${d.id}`); setDocs((x) => x.filter((i) => i.id !== d.id)); };

  return (
    <div data-testid="admin-kb">
      <PageHead eyebrow="AI Brain · RAG" title="AI Knowledge Base Manager" desc="Dokumen referensi yang dipakai Virtual Assistant. Setiap dokumen dipecah menjadi chunk dan diindeks untuk retrieval.">
        <button onClick={() => setAdding(true)} className="inline-flex items-center gap-2 bg-[#D97706] px-4 py-2.5 text-sm font-bold text-white hover:bg-[#B45309]" data-testid="kb-add-button"><Plus size={16} /> Tambah Dokumen</button>
      </PageHead>
      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        <Stat label="Dokumen" value={docs.length} testid="kb-stat-docs" />
        <Stat label="Chunk Terindeks" value={totalChunks} accent="text-[#0284C7]" testid="kb-stat-chunks" />
        <Stat label="Aktif untuk AI" value={docs.filter((d) => d.active).length} accent="text-emerald-600" testid="kb-stat-active" />
        <Stat label="Kategori" value={new Set(docs.map((d) => d.category)).size} testid="kb-stat-cats" />
      </div>
      <div className="mt-6 grid gap-6 xl:grid-cols-4">
        <div className="xl:col-span-3">
          <div className="mb-3 flex flex-wrap gap-2">
            {[["all", "Semua"], ...Object.entries(KB_CATS)].map(([k, l]) => (
              <button key={k} onClick={() => setCat(k)} data-testid={`kb-filter-${k}`} className={`border px-3 py-1.5 text-xs transition-colors ${cat === k ? "border-[#0F2B48] bg-[#0F2B48] text-white" : "border-slate-300 bg-white hover:border-[#0F2B48]"}`}>
                {l} ({k === "all" ? docs.length : docs.filter((d) => d.category === k).length})
              </button>
            ))}
          </div>
          <div className="overflow-x-auto border border-slate-200 bg-white">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-left font-mono text-[10px] uppercase tracking-widest text-slate-500">
                <tr><th className="p-3">Dokumen</th><th className="p-3">Kategori</th><th className="p-3">Status</th><th className="p-3">Chunk</th><th className="p-3">Aktif</th><th className="p-3 text-right">Aksi</th></tr>
              </thead>
              <tbody className="divide-y">
                {shown.map((d) => {
                  const Icon = SRC_ICON[d.source_type] || Database;
                  return (
                    <tr key={d.id} data-testid={`kb-row-${d.id}`} className="hover:bg-slate-50">
                      <td className="p-3"><div className="flex items-start gap-2"><Icon size={14} className="mt-1 shrink-0 text-[#0284C7]" /><div><p className="font-semibold text-[#0F2B48]">{d.title}</p><p className="max-w-xs truncate font-mono text-[10px] text-slate-500">{d.source_type.toUpperCase()} · {d.source} · {fmtDate(d.updated_at)}</p></div></div></td>
                      <td className="p-3 text-xs">{KB_CATS[d.category]}</td>
                      <td className="p-3"><span className={`px-2 py-0.5 font-mono text-[10px] uppercase ${d.status === "indexed" ? "bg-emerald-100 text-emerald-800" : "bg-amber-100 text-amber-800"}`}>{d.status}</span></td>
                      <td className="p-3 font-mono">{d.chunk_count}</td>
                      <td className="p-3"><Switch checked={d.active} onCheckedChange={() => toggle(d)} data-testid={`kb-toggle-${d.id}`} /></td>
                      <td className="p-3 text-right"><div className="inline-flex gap-1">
                        <button onClick={() => setViewId(d.id)} className="border p-2 hover:bg-slate-100" data-testid={`kb-view-${d.id}`}><Eye size={14} /></button>
                        <button onClick={() => reindex(d)} className="border p-2 hover:bg-slate-100" data-testid={`kb-reindex-${d.id}`}><RefreshCw size={14} /></button>
                        <button onClick={() => del(d)} className="border p-2 text-rose-600 hover:bg-rose-50" data-testid={`kb-delete-${d.id}`}><Trash2 size={14} /></button>
                      </div></td>
                    </tr>
                  );
                })}
                {!shown.length && <tr><td colSpan={6} className="p-8 text-center text-slate-500">Belum ada dokumen.</td></tr>}
              </tbody>
            </table>
          </div>
        </div>
        <RetrievalTester />
      </div>
      <AddDialog open={adding} onClose={() => setAdding(false)} onAdded={(d) => { upsert(d); setAdding(false); }} />
      <DocDialog docId={viewId} onClose={() => setViewId(null)} onUpdated={upsert} />
    </div>
  );
}
